import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { Theme } from '../../../../../theme';
import { EmployeeWithAvatar } from '../../../components/Employee';
import {
  SkillLevelLabel,
  SKILL_LEVEL_LABEL_HEIGHT,
} from '../common/SkillLevelLabel';
import {
  getClosestSkillLevel,
  getSkillLevelMedian,
} from '../common/skillLevels';
import { Employee, isSkillLevel, Skill } from '../common/types';
import {
  buildSkillGroups,
  computeGroupAverage,
  formatAverage,
  formatGroupAverageTooltip,
  SkillGroupBy,
} from '../common/skillGrouping';
import { formatMonthsExperience } from '../common/monthsExperience';
import { EmployeeSkillMatrix } from './types';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import InfoPopover from '../../../../../components/hint';

export type Props = {
  employees: Employee[];
  skills: Skill[];
  employeeSkills: EmployeeSkillMatrix[] | 'loading';
  grouping?: SkillGroupBy;
  resolveSkillName?: (skillId: string) => string | undefined;
  emptyEmployeesText?: string;
  onRemoveSkillClick: (skillId: string) => void;
  onRemoveEmployeeClick: (employeeId: string) => void;
  onEmployeeClick: (employee: Employee) => void;
  topLeftCell?: React.ReactNode;
};

type Column =
  | { kind: 'skill'; id: string; name: string; skill: Skill }
  | { kind: 'group'; id: string; name: string; skillIds: string[] };

export const SkillsMatrix: React.FC<React.PropsWithChildren<Props>> = ({
  employees,
  skills,
  employeeSkills,
  grouping = 'skill',
  resolveSkillName,
  emptyEmployeesText,
  onRemoveSkillClick,
  onRemoveEmployeeClick,
  topLeftCell,
  onEmployeeClick,
}) => {
  const t = useNamespacedTranslation('skills.matrix');
  const { t: tRoot } = useTranslation();

  const skillNameById = useCallback(
    (skillId: string): string =>
      resolveSkillName?.(skillId) ??
      skills.find((s) => s.id === skillId)?.name ??
      skillId,
    [resolveSkillName, skills],
  );

  const columns: Column[] = useMemo(() => {
    if (grouping === 'skill') {
      return skills.map((skill) => ({
        kind: 'skill',
        id: skill.id,
        name: skillNameById(skill.id),
        skill,
      }));
    }
    return buildSkillGroups(skills, grouping, skillNameById).map((group) => ({
      kind: 'group',
      id: group.key,
      name: group.name,
      skillIds: group.skillIds,
    }));
  }, [grouping, skills, skillNameById]);

  const shownEmployeeIds = useMemo(
    () => new Set(employees.map((e) => e.id)),
    [employees],
  );

  const renderHeader = () => {
    return (
      <>
        <StickyCell key="empty-header">
          <div style={{ zIndex: 1000 }}>
            {topLeftCell ? topLeftCell : <span>&nbsp;</span>}
          </div>
        </StickyCell>
        {columns.map((column) => (
          <HeaderColumnCell
            key={column.id}
            columnId={column.id}
            name={column.name}
            members={
              column.kind === 'group'
                ? column.skillIds.map(skillNameById)
                : undefined
            }
            onRemoveClick={
              column.kind === 'skill'
                ? () => onRemoveSkillClick(column.id)
                : undefined
            }
          />
        ))}
      </>
    );
  };

  const renderSkillCell = (
    column: Extract<Column, { kind: 'skill' }>,
    employee: Employee,
    employeeSkills: EmployeeSkillMatrix[],
  ) => {
    const key = `${employee.id}_${column.id}`;
    const employeeSkill = employeeSkills.find(
      (x) => x.employeeId === employee.id && x.skillId === column.id,
    );
    const experienceText = formatMonthsExperience(
      employeeSkill?.monthsExperience,
      tRoot,
    );
    const tooltipText = employeeSkill
      ? [
          employeeSkill.level != null
            ? `${tRoot('skills.matrix.tooltipLevel')}: ${employeeSkill.level}`
            : null,
          experienceText
            ? `${tRoot('skills.matrix.tooltipExperience')}: ${experienceText}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : undefined;
    return (
      <CellContainer key={key} title={tooltipText || undefined}>
        <SkillLevelLabel
          value={employeeSkill ? employeeSkill.level : undefined}
          width="100%"
        />
        <ExperienceSubtext>{experienceText || ' '}</ExperienceSubtext>
      </CellContainer>
    );
  };

  const renderGroupCell = (
    column: Extract<Column, { kind: 'group' }>,
    employee: Employee,
    employeeSkills: EmployeeSkillMatrix[],
  ) => {
    const key = `${employee.id}_${column.id}`;
    const avg = computeGroupAverage(
      column.skillIds,
      (skillId) =>
        employeeSkills.find(
          (x) => x.employeeId === employee.id && x.skillId === skillId,
        )?.level,
      skillNameById,
    );
    return (
      <CellContainer
        key={key}
        title={formatGroupAverageTooltip(avg) || undefined}>
        <SkillLevelLabel
          value={
            avg.average !== null ? getClosestSkillLevel(avg.average) : undefined
          }
          width="100%"
          title={formatGroupAverageTooltip(avg) || undefined}
          displayValue={
            avg.average !== null ? formatAverage(avg.average) : undefined
          }
        />
        <ExperienceSubtext> </ExperienceSubtext>
      </CellContainer>
    );
  };

  const renderEmployeeRow = (
    employee: Employee,
    employeeSkills: EmployeeSkillMatrix[],
  ) => {
    return (
      <React.Fragment key={`employee-${employee.id}`}>
        <EmployeeCell
          employee={employee}
          onRemoveEmployeeClick={onRemoveEmployeeClick}
          onClick={onEmployeeClick}
        />
        {columns.map((column) =>
          column.kind === 'skill'
            ? renderSkillCell(column, employee, employeeSkills)
            : renderGroupCell(column, employee, employeeSkills),
        )}
      </React.Fragment>
    );
  };

  const renderLoadingRow = (employee: Employee) => {
    return (
      <React.Fragment key={`loading-${employee.id}`}>
        <EmployeeCell
          employee={employee}
          onRemoveEmployeeClick={onRemoveEmployeeClick}
          onClick={() => {}}
        />
        {columns.map((column) => (
          <LoadingCell key={column.id} />
        ))}
      </React.Fragment>
    );
  };

  const renderNoEmployeesRow = () => {
    return (
      <>
        <NoEmployeesCell>
          <Hint>{emptyEmployeesText ?? t('emptyEmployees')}</Hint>
        </NoEmployeesCell>
        {columns.length > 0 ? (
          columns.map((column) => <div key={column.id} />)
        ) : (
          <div></div>
        )}
      </>
    );
  };

  const renderSkillFooterCell = (
    column: Extract<Column, { kind: 'skill' }>,
    employeeSkills: EmployeeSkillMatrix[],
  ) => {
    const allSkillsOfColumn = employeeSkills.filter(
      (x) => x.skillId === column.id && shownEmployeeIds.has(x.employeeId),
    );
    const allLevels = allSkillsOfColumn
      .map((x) => x.level)
      .filter(isSkillLevel);
    const median = getSkillLevelMedian(allLevels);
    return (
      <CellContainer key={`median_${column.id}`}>
        <SkillLevelLabel
          value={
            median !== undefined ? getClosestSkillLevel(median) : undefined
          }
          width="100%"
          displayValue={median !== undefined ? median.toString() : undefined}
        />
      </CellContainer>
    );
  };

  const renderGroupFooterCell = (
    column: Extract<Column, { kind: 'group' }>,
    employeeSkills: EmployeeSkillMatrix[],
  ) => {
    const employeeAverages = employees
      .map(
        (employee) =>
          computeGroupAverage(
            column.skillIds,
            (skillId) =>
              employeeSkills.find(
                (x) => x.employeeId === employee.id && x.skillId === skillId,
              )?.level,
            skillNameById,
          ).average,
      )
      .filter((avg): avg is number => avg !== null);
    const teamAverage =
      employeeAverages.length > 0
        ? employeeAverages.reduce((acc, avg) => acc + avg, 0) /
          employeeAverages.length
        : null;
    return (
      <CellContainer key={`team_${column.id}`}>
        <SkillLevelLabel
          value={
            teamAverage !== null ? getClosestSkillLevel(teamAverage) : undefined
          }
          width="100%"
          displayValue={
            teamAverage !== null ? formatAverage(teamAverage) : undefined
          }
        />
      </CellContainer>
    );
  };

  const renderFooter = (employeeSkills: EmployeeSkillMatrix[]) => {
    return (
      <>
        <SummaryCell key="summary">
          <div>{grouping === 'skill' ? t('median') : t('teamLevel')}</div>
          <Hint>{t('ofNEmployees', { count: employees.length })}</Hint>
        </SummaryCell>
        {columns.map((column) =>
          column.kind === 'skill'
            ? renderSkillFooterCell(column, employeeSkills)
            : renderGroupFooterCell(column, employeeSkills),
        )}
      </>
    );
  };

  const renderLoadingFooter = () => {
    return (
      <>
        <SummaryCell key="summary">
          <div>{grouping === 'skill' ? t('median') : t('teamLevel')}</div>
          <Hint>{t('ofNEmployees', { count: employees.length })}</Hint>
        </SummaryCell>
        {columns.map((column) => (
          <LoadingCell key={column.id} />
        ))}
      </>
    );
  };

  const renderNoSkills = () => {
    return (
      <NoSkillsContainer employeesCount={employees.length}>
        <div>{t('emptySkills')}</div>
        <Hint>{t('emptySkillsSubline')}</Hint>
      </NoSkillsContainer>
    );
  };

  const isLoading = employeeSkills === 'loading';

  return (
    <Grid skillsCount={columns.length} employeesCount={employees.length}>
      {columns.length === 0 && renderNoSkills()}
      {renderHeader()}
      {employees.length > 0 &&
        employees.map((employee) =>
          !isLoading
            ? renderEmployeeRow(employee, employeeSkills)
            : renderLoadingRow(employee),
        )}
      {employees.length === 0 && renderNoEmployeesRow()}
      {!isLoading ? renderFooter(employeeSkills) : renderLoadingFooter()}
      <FooterBorder
        skillsCount={columns.length}
        employeesCount={employees.length}>
        &nbsp;
      </FooterBorder>
    </Grid>
  );
};

type HeaderColumnCellProps = {
  columnId: string;
  name: string;
  members?: string[];
  onRemoveClick?: () => void;
};

const HeaderColumnCell: React.FC<
  React.PropsWithChildren<HeaderColumnCellProps>
> = ({ columnId, name, members, onRemoveClick }) => {
  const tGrouping = useNamespacedTranslation('skills.grouping');
  return (
    <HeaderCell className="fs-5 fw-bold d-flex justify-content-between align-items-center flex-nowrap gap-1">
      <div title={name} className="text-truncate">
        {name}
      </div>
      <div className="d-flex align-items-center gap-1 flex-nowrap">
        {members && members.length > 0 && (
          <GroupInfo>
            <InfoPopover
              id={`group-${columnId}`}
              mode="popover"
              text={
                <div className="text-start">
                  <div className="fw-bold mb-1">
                    {tGrouping('skillsHeader')}
                  </div>
                  {members.map((member, index) => (
                    <div key={`${columnId}-${index}`}>{member}</div>
                  ))}
                </div>
              }>
              <FlairIcon
                icon="information-circle-outline"
                size="sm"
                role="button"
                color={Theme.color.dark}
              />
            </InfoPopover>
          </GroupInfo>
        )}
        {onRemoveClick && (
          <FlairIcon
            icon="close-outline"
            role="button"
            onClick={onRemoveClick}
            color={Theme.color.paper1}
            cursor="pointer"
          />
        )}
      </div>
    </HeaderCell>
  );
};

type EmployeeCellProps = {
  employee: Employee;
  onRemoveEmployeeClick: (employeeId: string) => void;
  onClick: (employee: Employee) => void;
};

const EmployeeCell: React.FC<React.PropsWithChildren<EmployeeCellProps>> = ({
  employee,
  onRemoveEmployeeClick,
  onClick,
}) => {
  const handleEmployeeClick = useCallback(() => {
    onClick(employee);
  }, [onClick, employee]);

  const handleRemoveeEmployee = useCallback(() => {
    onRemoveEmployeeClick(employee.id);
  }, [onRemoveEmployeeClick, employee]);

  return (
    <StickyCell className="d-flex align-items-center flex-nowrap small">
      <div onClick={handleEmployeeClick} role="button">
        <EmployeeWithAvatar
          employee={{
            name: employee.name,
            avatarUrl: employee.avatarUrl,
          }}
        />
      </div>
      <FlairIcon
        icon="close-outline"
        className="ms-2"
        onClick={handleRemoveeEmployee}
        color={Theme.color.paper1}
        cursor="pointer"
      />
    </StickyCell>
  );
};

const LoadingCell: React.FC<React.PropsWithChildren<unknown>> = () => (
  <CellContainer>
    <Skeleton width="100%" height={SKILL_LEVEL_LABEL_HEIGHT} />
  </CellContainer>
);

const Grid = styled.div<{ skillsCount: number; employeesCount: number }>(
  {
    display: 'grid',
    gridGap: '1rem',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
  },
  ({ skillsCount, employeesCount }) => ({
    gridTemplateRows: `repeat(${employeesCount + 2}, auto)`,
    gridTemplateColumns:
      skillsCount > 0
        ? `minmax(auto, 13rem) repeat(${skillsCount}, minmax(5rem, 1fr))`
        : 'minmax(auto, 13rem) auto',
  }),
);

const NoSkillsContainer = styled.div<{
  employeesCount: number;
}>(
  {
    gridColumn: '2 / 3',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ({ employeesCount }) => ({
    gridRow: `1 / span ${employeesCount + 1}`,
  }),
);

const GroupInfo = styled.span({
  display: 'inline-flex',
});

const HeaderCell = styled.div({});

const StickyCell = styled.div({
  position: 'sticky',
  left: 0,
  backgroundColor: 'white',
  paddingRight: '0.75rem',
  paddingLeft: '1.5rem',
});

const NoEmployeesCell = styled(StickyCell)({
  minWidth: '13rem',
});

const SummaryCell = ({
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) => (
  <StickyCell
    {...rest}
    className={classNames('fs-5', 'fw-bold', 'text-end', className)}
  />
);

const Hint = ({ className, ...rest }: React.HTMLAttributes<HTMLDivElement>) => (
  <div {...rest} className={classNames('fs-5', 'text-dark')} />
);

const CellContainer = styled.div({
  lineHeight: 1,
});

const ExperienceSubtext = styled.div({
  marginTop: '0.25rem',
  fontSize: '0.65rem',
  lineHeight: '1rem',
  minHeight: '1rem',
  color: Theme.color.dark,
  textAlign: 'center',
});

const FooterBorder = styled('div')<{
  skillsCount: number;
  employeesCount: number;
}>(
  {
    position: 'absolute',
    top: 0,
    marginTop: '-0.325rem',
    borderColor: Theme.color.paper2,
    borderTopWidth: '1px',
    borderTopStyle: 'dashed',
    marginLeft: '1.5rem',
    width: '100%',
  },
  ({ skillsCount, employeesCount }) => ({
    gridRow: employeesCount > 0 ? employeesCount + 2 : 3,
    gridColumn: `1 / ${skillsCount > 0 ? skillsCount + 2 : 3}`,
  }),
);
